import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Progress } from "../components/ui/progress";
import { 
  Target, 
  Phone, 
  TrendingUp, 
  Users, 
  Clock,
  ArrowRight,
  CheckCircle2,
  XCircle,
  Zap,
  Activity,
  ListTodo
} from "lucide-react";
import { ActivityFeedWidget } from "./ActivityFeedPage";
import TelecomUsageWidget from "../components/TelecomUsageWidget";
import RecentEmailsWidget from "../components/RecentEmailsWidget";
import CampaignsWidget from "../components/CampaignsWidget";
import MyImportsWidget from "../components/MyImportsWidget";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusColors = {
  prospect: "status-prospect",
  contacted: "status-contacted",
  qualified: "status-qualified",
  closed_won: "status-closed-won",
  closed_lost: "status-closed-lost"
};

const statusLabels = {
  prospect: "Prospect",
  contacted: "Contacted",
  qualified: "Qualified",
  closed_won: "Closed Won",
  closed_lost: "Closed Lost"
};

const outcomeIcons = {
  answered: CheckCircle2,
  no_answer: XCircle,
  voicemail: Phone,
  busy: Clock,
  wrong_number: XCircle
};

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [activity, setActivity] = useState(null);
  const [usage, setUsage] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [statsRes, activityRes, usageRes, tasksRes] = await Promise.all([
        fetch(`${API}/dashboard/stats`, { credentials: "include" }),
        fetch(`${API}/dashboard/recent-activity`, { credentials: "include" }),
        fetch(`${API}/usage/extractions`, { credentials: "include" }),
        fetch(`${API}/tasks?status=pending`, { credentials: "include" })
      ]);

      if (statsRes.ok && activityRes.ok) {
        const statsData = await statsRes.json();
        const activityData = await activityRes.json();
        setStats(statsData);
        setActivity(activityData);
      }
      
      if (usageRes.ok) {
        const usageData = await usageRes.json();
        setUsage(usageData);
      }

      if (tasksRes.ok) {
        const tasksData = await tasksRes.json();
        setTasks(tasksData.tasks || []);
      }
    } catch (error) {
      console.error("Failed to fetch dashboard data:", error);
    } finally {
      setLoading(false);
    }
  };

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    return `${diffDays}d ago`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in-up" data-testid="dashboard">
      {/* Page header */}
      <div>
        <h1 className="font-heading text-3xl font-bold tracking-tight text-slate-900">
          Dashboard
        </h1>
        <p className="text-slate-500 mt-1">
          Welcome back! Here's your sales overview.
        </p>
      </div>

      {/* Stats grid */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 stagger-children">
        <Card className="metric-card" data-testid="stat-total-leads">
          <div className="flex items-start justify-between">
            <div>
              <p className="metric-label">Total Leads</p>
              <p className="metric-value">{stats?.total_leads || 0}</p>
            </div>
            <div className="p-2 bg-primary/10 rounded-md">
              <Target className="w-5 h-5 text-primary" />
            </div>
          </div>
        </Card>

        <Card className="metric-card" data-testid="stat-calls-today">
          <div className="flex items-start justify-between">
            <div>
              <p className="metric-label">Calls Today</p>
              <p className="metric-value">{stats?.calls_today || 0}</p>
            </div>
            <div className="p-2 bg-blue-50 rounded-md">
              <Phone className="w-5 h-5 text-blue-600" />
            </div>
          </div>
        </Card>

        <Card className="metric-card" data-testid="stat-new-leads">
          <div className="flex items-start justify-between">
            <div>
              <p className="metric-label">New This Week</p>
              <p className="metric-value">{stats?.new_leads_week || 0}</p>
            </div>
            <div className="p-2 bg-emerald-50 rounded-md">
              <Users className="w-5 h-5 text-emerald-600" />
            </div>
          </div>
        </Card>

        <Card className="metric-card" data-testid="stat-conversion">
          <div className="flex items-start justify-between">
            <div>
              <p className="metric-label">Conversion Rate</p>
              <p className="metric-value">{stats?.conversion_rate || 0}%</p>
            </div>
            <div className="p-2 bg-indigo-50 rounded-md">
              <TrendingUp className="w-5 h-5 text-indigo-600" />
            </div>
          </div>
        </Card>

        {/* Daily Usage */}
        <Card className="metric-card" data-testid="stat-daily-usage">
          <div className="flex items-start justify-between">
            <div className="w-full">
              <p className="metric-label">Daily Leads</p>
              <p className="metric-value">
                {usage?.my_usage?.used || 0}/{usage?.my_usage?.limit || 10}
              </p>
              <Progress 
                value={((usage?.my_usage?.used || 0) / (usage?.my_usage?.limit || 10)) * 100} 
                className="mt-2 h-1.5"
              />
            </div>
            <div className="p-2 bg-amber-50 rounded-md ml-2">
              <Zap className="w-5 h-5 text-amber-600" />
            </div>
          </div>
        </Card>
      </div>

      {/* Pipeline overview */}
      <Card data-testid="pipeline-overview">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="font-heading text-lg font-bold tracking-tight">
              Pipeline Overview
            </CardTitle>
            <button 
              onClick={() => navigate("/leads")}
              className="text-sm text-primary hover:underline flex items-center gap-1"
              data-testid="view-all-leads-btn"
            >
              View all <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-5 gap-2">
            {Object.entries(statusLabels).map(([key, label]) => (
              <div 
                key={key} 
                className="text-center p-3 bg-slate-50 rounded-lg cursor-pointer hover:bg-slate-100 transition-colors"
                onClick={() => navigate(`/leads?status=${key}`)}
                data-testid={`pipeline-${key}`}
              >
                <p className="font-heading text-2xl font-bold text-slate-900">
                  {stats?.by_status?.[key] || 0}
                </p>
                <p className="text-xs text-slate-500 mt-1">{label}</p>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Telecom usage widget */}
      <TelecomUsageWidget />

      {/* Two column layout */}
      <div className="grid lg:grid-cols-3 gap-6">
        {/* Recent calls */}
        <Card data-testid="recent-calls">
          <CardHeader className="pb-3">
            <CardTitle className="font-heading text-lg font-bold tracking-tight">
              Recent Calls
            </CardTitle>
          </CardHeader>
          <CardContent>
            {activity?.recent_calls?.length === 0 ? (
              <p className="text-slate-500 text-sm py-4 text-center">
                No calls logged yet. Start making calls!
              </p>
            ) : (
              <div className="space-y-3">
                {activity?.recent_calls?.slice(0, 5).map((call) => {
                  const OutcomeIcon = outcomeIcons[call.outcome] || Phone;
                  return (
                    <div 
                      key={call.call_id}
                      className="flex items-center gap-3 p-2 rounded-md hover:bg-slate-50 cursor-pointer"
                      onClick={() => navigate(`/leads/${call.lead_id}`)}
                    >
                      <div className={`p-1.5 rounded-md ${
                        call.outcome === "answered" ? "bg-emerald-50" : "bg-slate-100"
                      }`}>
                        <OutcomeIcon className={`w-4 h-4 ${
                          call.outcome === "answered" ? "text-emerald-600" : "text-slate-500"
                        }`} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-slate-900 truncate">
                          {call.business_name}
                        </p>
                        <p className="text-xs text-slate-500">
                          {call.user_name} • {call.outcome.replace("_", " ")}
                        </p>
                      </div>
                      <span className="text-xs text-slate-400">
                        {formatTime(call.call_time)}
                      </span>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Recent leads */}
        <Card data-testid="recent-leads">
          <CardHeader className="pb-3">
            <CardTitle className="font-heading text-lg font-bold tracking-tight">
              Recent Leads
            </CardTitle>
          </CardHeader>
          <CardContent>
            {activity?.recent_leads?.length === 0 ? (
              <p className="text-slate-500 text-sm py-4 text-center">
                No leads yet. Search for businesses to get started!
              </p>
            ) : (
              <div className="space-y-3">
                {activity?.recent_leads?.slice(0, 5).map((lead) => (
                  <div 
                    key={lead.lead_id}
                    className="flex items-center gap-3 p-2 rounded-md hover:bg-slate-50 cursor-pointer"
                    onClick={() => navigate(`/leads/${lead.lead_id}`)}
                  >
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-slate-900 truncate">
                        {lead.business_name}
                      </p>
                      <p className="text-xs text-slate-500">
                        {lead.city}, {lead.state}
                      </p>
                    </div>
                    <Badge className={`${statusColors[lead.status]} text-xs`}>
                      {statusLabels[lead.status]}
                    </Badge>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Recent tasks */}
        <Card data-testid="recent-tasks">
          <CardHeader className="pb-3">
            <CardTitle className="font-heading text-lg font-bold tracking-tight flex items-center gap-2">
              <ListTodo className="w-4 h-4" />
              Recent Tasks
            </CardTitle>
          </CardHeader>
          <CardContent>
            {tasks.length === 0 ? (
              <p className="text-slate-500 text-sm py-4 text-center">
                No pending tasks. You&apos;re all caught up!
              </p>
            ) : (
              <div className="space-y-3">
                {tasks.slice(0, 5).map((task) => (
                  <div
                    key={task.task_id}
                    className="flex items-center gap-3 p-2 rounded-md hover:bg-slate-50 cursor-pointer"
                    onClick={() => navigate(task.lead_id ? `/leads/${task.lead_id}` : "/tasks")}
                    data-testid={`recent-task-${task.task_id}`}
                  >
                    <div className={`p-1.5 rounded-md ${
                      task.priority === "high" ? "bg-red-50" : task.priority === "low" ? "bg-slate-100" : "bg-amber-50"
                    }`}>
                      <ListTodo className={`w-4 h-4 ${
                        task.priority === "high" ? "text-red-600" : task.priority === "low" ? "text-slate-500" : "text-amber-600"
                      }`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-slate-900 truncate">
                        {task.title}
                      </p>
                      <p className="text-xs text-slate-500 truncate">
                        {task.lead_name || "No lead"}
                        {task.due_date && ` · due ${new Date(task.due_date).toLocaleDateString()}`}
                      </p>
                    </div>
                    <Badge variant="outline" className="text-[10px] uppercase">
                      {task.priority}
                    </Badge>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Recent Emails */}
      <RecentEmailsWidget />

      {/* My Imports This Week */}
      <MyImportsWidget />

      {/* Campaigns */}
      <CampaignsWidget />

      {/* Activity Feed Widget */}
      <Card data-testid="dashboard-activity-feed">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Activity className="w-4 h-4" />
            Team Activity
          </CardTitle>
        </CardHeader>
        <CardContent>
          <ActivityFeedWidget />
        </CardContent>
      </Card>
    </div>
  );
}
